import React, { useState, useRef, useEffect } from 'react';
import * as Tone from 'tone';
import * as acorn from 'acorn';

const SonicCodeEngine = ({ initialCode = '', readOnly = false }) => {
  const [code, setCode] = useState(
    initialCode || `// Write your code here and push Play Code Sonically
// Try to listen to the difference between variables, loops, and conditions!

let score = 0;

function checkScore() {
  for(let i = 0; i < 3; i++) {
    if (score < 5) {
      score = score + 1;
    }
  }
}

checkScore();
`
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => {
    if (initialCode) {
      setCode(initialCode);
    }
  }, [initialCode]);
  
  // Ref to hold synths so we can clean them up and use them in callbacks
  const synthsRef = useRef({});

  useEffect(() => {
    // Initialize synthesizers for different code constructs
    synthsRef.current = {
      // Variables and Expressions
      variable: new Tone.Synth({
        oscillator: { type: 'triangle' },
        envelope: { attack: 0.05, decay: 0.1, sustain: 0.3, release: 1 }
      }).toDestination(),
      
      // Loops (For / While)
      loop: new Tone.MembraneSynth().toDestination(),
      
      // Conditionals (If/Else)
      conditional: new Tone.FMSynth().toDestination(),
      
      // Errors/Bugs
      errorSynth: new Tone.AMSynth({
         oscillator: { type: 'sawtooth' }
      }).toDestination(),
      
      // Functions
      functionPlay: new Tone.PluckSynth().toDestination(),
    };

    return () => {
      // Cleanup synthesizers on unmount to avoid memory leaks
      Object.keys(synthsRef.current).forEach(key => {
          if (synthsRef.current[key]) {
              synthsRef.current[key].dispose();
          }
      });
    };
  }, []);

  const parseAndPlay = async () => {
    if (isPlaying) {
      Tone.Transport.stop();
      Tone.Transport.cancel();
      setIsPlaying(false);
      return;
    }

    // Required by browsers to start audio context upon user interaction
    await Tone.start(); 
    Tone.Transport.cancel(); // Clear any previous events
    setErrorStatus(null);

    let ast;
    try {
      // Parse the user's javascript code into an Abstract Syntax Tree (AST)
      // This is the magic that understands the code structure, not just the text.
      ast = acorn.parse(code, { ecmaVersion: 2020 });
    } catch (err) {
      // If there's a syntax error, play the harsh error sound!
      setErrorStatus(`Syntax Error: ${err.message}`);
      if (synthsRef.current.errorSynth) {
          synthsRef.current.errorSynth.triggerAttackRelease('C2', '8n');
      }
      return;
    }

    setIsPlaying(true);
    let timeOffset = 0; // The current time offset into the Tone Transport
    const timeStep = 0.4; // Seconds per node read

    // Recursive function to schedule sounds based on AST nodes mapped to specific synths
    const traverse = (node) => {
      if (!node) return;

      switch(node.type) {
        case 'VariableDeclaration':
          Tone.Transport.schedule((time) => {
             // Play a pluck sound for variable declaration
             synthsRef.current.variable.triggerAttackRelease("C4", "8n", time);
          }, timeOffset);
          timeOffset += timeStep;
          break;
        case 'ForStatement':
        case 'WhileStatement':
          Tone.Transport.schedule((time) => {
             // Deep kick sound for loops
             synthsRef.current.loop.triggerAttackRelease("C1", "8n", time);
          }, timeOffset);
          timeOffset += timeStep;
          // Traverse loop body
          traverse(node.body);
          break;
        case 'IfStatement':
          Tone.Transport.schedule((time) => {
             // High pitched bell-like sound for condition
             synthsRef.current.conditional.triggerAttackRelease("E5", "8n", time);
          }, timeOffset);
          timeOffset += timeStep;
          // Traverse if block
          traverse(node.consequent);
          // Traverse else block if it exists
          if (node.alternate) traverse(node.alternate);
          break;
        case 'FunctionDeclaration':
          Tone.Transport.schedule((time) => {
             // Pluck for function definition
             synthsRef.current.functionPlay.triggerAttackRelease("G4", "8n", time);
          }, timeOffset);
          timeOffset += timeStep;
          traverse(node.body);
          break;
        case 'ExpressionStatement':
        case 'CallExpression':
          Tone.Transport.schedule((time) => {
             // Short variable tick for general expressions and function calls
             synthsRef.current.variable.triggerAttackRelease("A4", "16n", time);
          }, timeOffset);
          timeOffset += timeStep;
          break;
        case 'BlockStatement':
          node.body.forEach(traverse);
          break;
        default:
          // We can recursively look deeper into unknown nodes if they hold blocks,
          // but for basic mapping we skip to maintain rhythm flow.
          break;
      }
    };

    // Begin AST traversal from the root body
    if (ast.body) {
      ast.body.forEach(traverse);
    }

    // Stop recording state after the calculated duration
    Tone.Transport.schedule((time) => {
      Tone.Draw.schedule(() => {
        setIsPlaying(false);
        Tone.Transport.stop();
      }, time);
    }, timeOffset + timeStep);

    // Start playing all scheduled events!
    Tone.Transport.start();
  };

  return (
    <div style={{ 
      padding: '24px', 
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)', 
      color: '#f8fafc', 
      borderRadius: '12px', 
      maxWidth: '700px',
      margin: '0 auto',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)'
    }}>
      <h2 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '24px', 
        fontWeight: 'bold', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px' 
      }}>
        🎹 The Sonic Code Engine (Demo)
      </h2>


      {/* Hide textarea if readOnly is true, else show it */}
      {!readOnly && (
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          spellCheck="false"
          style={{ 
            width: '100%', 
            height: '250px', 
            padding: '16px', 
            background: '#020617', 
            color: '#38bdf8', 
            border: '1px solid #334155', 
            fontFamily: 'Consolas, Monaco, "Andale Mono", monospace', 
            fontSize: '14px',
            borderRadius: '8px',
            boxSizing: 'border-box',
            resize: 'vertical',
            marginBottom: '10px'
          }}
        />
      )}
      
      {errorStatus && (
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.1)', 
          color: '#ef4444', 
          padding: '12px', 
          marginTop: '12px', 
          borderRadius: '6px',
          borderLeft: '4px solid #ef4444'
        }}>
           <strong>⚠ Bug Detected:</strong> {errorStatus}
        </div>
      )}

      <button 
        onClick={parseAndPlay}
        style={{
          marginTop: '20px', 
          width: '100%',
          padding: '14px 24px', 
          background: isPlaying ? '#ef4444' : '#8b5cf6', 
          color: 'white', 
          border: 'none', 
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '16px',
          transition: 'all 0.2s',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {isPlaying ? "⏹ Stop Playback" : "🔊 Scan & Play Code Sonically"}
      </button>
    </div>
  );
};

export default SonicCodeEngine;
